import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

type TSearchParamsInit = URLSearchParams | Record<string, string | string[]> | string;

interface ISetSearchParamsOptions {
  replace?: boolean;
}

type TSetSearchParams = (
  nextInit: TSearchParamsInit | ((prev: URLSearchParams) => TSearchParamsInit),
  navigateOptions?: ISetSearchParamsOptions
) => void;

/**
 * Convierte un objeto plano en URLSearchParams.
 * @param init - Objeto con valores string o arreglo de strings
 */
const recordToSearchParams = (init: Record<string, string | string[]>): URLSearchParams => {
  const params = new URLSearchParams();

  Object.entries(init).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(item => params.append(key, item));
      return;
    }
    params.set(key, value);
  });

  return params;
};

/**
 * Normaliza el valor recibido por setSearchParams a URLSearchParams.
 * @param nextInit - Valor inicial o función actualizadora
 * @param currentParams - Params actuales de la URL
 */
const resolveSearchParams = (
  nextInit: TSearchParamsInit | ((prev: URLSearchParams) => TSearchParamsInit),
  currentParams: URLSearchParams
): URLSearchParams => {
  const resolved = typeof nextInit === 'function' ? nextInit(currentParams) : nextInit;

  if (resolved instanceof URLSearchParams) return resolved;
  if (typeof resolved === 'string') return new URLSearchParams(resolved);
  return recordToSearchParams(resolved);
};

/**
 * Hook compatible con la API de useSearchParams de React Router v6.
 * Usa history y location de react-router-dom v5 bajo el capó.
 * @returns Tupla [searchParams, setSearchParams]
 */
export const useSearchParams = (): [URLSearchParams, TSetSearchParams] => {
  const location = useLocation();
  const history = useHistory();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const setSearchParams = useCallback<TSetSearchParams>(
    (nextInit, navigateOptions) => {
      const currentParams = new URLSearchParams(location.search);
      const nextParams = resolveSearchParams(nextInit, currentParams);
      const search = nextParams.toString();
      const navigation = {
        pathname: location.pathname,
        search: search ? `?${search}` : ''
      };

      if (navigateOptions?.replace) {
        history.replace(navigation);
        return;
      }

      history.push(navigation);
    },
    [history, location.pathname, location.search]
  );

  return [searchParams, setSearchParams];
};
